import type {
  LayoutPatchOperation,
  PolicyValidationResult,
} from '@shared/contracts/customisation';
import {
  MOBILE_WIDGETS,
  defaultWidgetOrder,
  enforcePinning,
  isKnownWidget,
  widgetById,
  type MobileWidgetId,
} from './widgetRegistry';
import { CAPABILITIES, resolveCapability, type EntitlementContext } from '@/services/entitlements';

/**
 * Deterministic layout policy engine.
 *
 * Binding rule (13_AI_INTERFACE_COMPOSER_SPEC_V5.md):
 * "A deterministic policy engine — not the model — authorises operations."
 *
 * Everything that can change a layout, whether typed by a human or proposed by
 * the assistant, is validated here. The model's own `policyResult` is never
 * trusted; it is recomputed.
 */

export const POLICY_VERSION = 1;

export type LayoutState = {
  readonly order: readonly MobileWidgetId[];
  readonly version: number;
  readonly etag: string;
};

/** Widgets the user is entitled to see at all. */
export function entitledWidgetIds(ctx: EntitlementContext): readonly MobileWidgetId[] {
  return MOBILE_WIDGETS.filter((w) => {
    if (!w.capability) return true;
    return resolveCapability(ctx, CAPABILITIES[w.capability]).allowed;
  }).map((w) => w.id);
}

/**
 * Validate a set of operations against the current order.
 *
 * Returns violations rather than throwing, so the UI can show an
 * operation-by-operation diff with the reasons attached.
 */
export function validateOperations(
  ctx: EntitlementContext,
  currentOrder: readonly MobileWidgetId[],
  operations: readonly LayoutPatchOperation[],
): PolicyValidationResult {
  const violations: string[] = [];
  const warnings: string[] = [];
  const entitled = new Set(entitledWidgetIds(ctx));

  let order = [...currentOrder];

  for (const op of operations) {
    switch (op.type) {
      case 'add': {
        const id = op.widgetId;
        if (!id || !isKnownWidget(id)) {
          violations.push(`Unknown widget "${String(id)}" cannot be added.`);
          break;
        }
        if (!entitled.has(id)) {
          violations.push(`You are not entitled to the "${widgetById(id)?.title ?? id}" widget.`);
          break;
        }
        if (order.includes(id)) {
          warnings.push(`"${widgetById(id)?.title ?? id}" is already on your home.`);
          break;
        }
        order.push(id);
        break;
      }
      case 'remove': {
        const id = op.widgetId ?? op.instanceId;
        if (!id || !isKnownWidget(id)) {
          violations.push(`Unknown widget "${String(id)}" cannot be removed.`);
          break;
        }
        const definition = widgetById(id);
        if (definition && !definition.removable) {
          // Mandatory controls are locked (11_BINDING_USER_ADDENDUM_V5).
          violations.push(
            `"${definition.title}" is a required part of your home and cannot be removed.`,
          );
          break;
        }
        order = order.filter((w) => w !== id);
        break;
      }
      case 'move': {
        const id = op.widgetId ?? op.instanceId;
        if (!id || !isKnownWidget(id)) {
          violations.push(`Unknown widget "${String(id)}" cannot be moved.`);
          break;
        }
        const definition = widgetById(id);
        if (definition?.pinnedIndex !== undefined) {
          violations.push(`"${definition.title}" has a fixed position and cannot be moved.`);
          break;
        }
        const target = op.position?.y;
        if (typeof target !== 'number' || target < 0 || target > order.length) {
          violations.push(`Invalid position for "${definition?.title ?? id}".`);
          break;
        }
        order = order.filter((w) => w !== id);
        order.splice(Math.min(target, order.length), 0, id);
        break;
      }
      case 'resize': {
        // Mobile is single-column: every widget is full width. A resize is
        // accepted as a no-op rather than silently distorting the layout.
        warnings.push('Widget sizes are fixed on mobile, so that change was ignored.');
        break;
      }
      case 'configure': {
        const id = op.widgetId ?? op.instanceId;
        const definition = id && isKnownWidget(id) ? widgetById(id) : undefined;
        if (!definition) {
          violations.push(`Unknown widget "${String(id)}" cannot be configured.`);
          break;
        }
        for (const key of Object.keys(op.configPatch ?? {})) {
          if (!definition.allowedConfigKeys.includes(key)) {
            violations.push(`"${key}" is not a configurable option on "${definition.title}".`);
          }
        }
        break;
      }
      default: {
        violations.push('Unsupported layout operation.');
      }
    }
  }

  // The resulting layout must still satisfy the hard ordering constraints and
  // must still contain every non-removable widget.
  const pinned = enforcePinning(order);
  for (const w of MOBILE_WIDGETS) {
    if (!w.removable && entitled.has(w.id) && !pinned.includes(w.id)) {
      violations.push(`"${w.title}" is required and must stay on your home.`);
    }
  }

  return {
    valid: violations.length === 0,
    violations,
    warnings,
    evaluatedPolicyVersion: POLICY_VERSION,
  };
}

/** Apply operations that have already passed validation. */
export function applyOperations(
  currentOrder: readonly MobileWidgetId[],
  operations: readonly LayoutPatchOperation[],
): readonly MobileWidgetId[] {
  let order = [...currentOrder];
  for (const op of operations) {
    const id = (op.widgetId ?? op.instanceId) as MobileWidgetId | undefined;
    if (!id || !isKnownWidget(id)) continue;
    if (op.type === 'add' && !order.includes(id)) order.push(id);
    if (op.type === 'remove') order = order.filter((w) => w !== id);
    if (op.type === 'move' && typeof op.position?.y === 'number') {
      order = order.filter((w) => w !== id);
      order.splice(Math.min(op.position.y, order.length), 0, id);
    }
  }
  return enforcePinning(order);
}

/** Human-readable diff, one line per operation, labelled by verb not colour. */
export type DiffLine = {
  readonly kind: 'Add' | 'Remove' | 'Move' | 'Resize' | 'Configure';
  readonly text: string;
};

export function describeOperations(
  currentOrder: readonly MobileWidgetId[],
  operations: readonly LayoutPatchOperation[],
): readonly DiffLine[] {
  const lines: DiffLine[] = [];
  let order = [...currentOrder];

  for (const op of operations) {
    const id = (op.widgetId ?? op.instanceId) as MobileWidgetId | undefined;
    const title = id && isKnownWidget(id) ? (widgetById(id)?.title ?? id) : String(id);
    switch (op.type) {
      case 'add':
        lines.push({ kind: 'Add', text: `Add "${title}" to your home` });
        if (id && isKnownWidget(id)) order.push(id);
        break;
      case 'remove':
        lines.push({ kind: 'Remove', text: `Remove "${title}" from your home` });
        order = order.filter((w) => w !== id);
        break;
      case 'move': {
        const to = (op.position?.y ?? 0) + 1;
        lines.push({ kind: 'Move', text: `Move "${title}" to position ${to}` });
        break;
      }
      case 'resize':
        lines.push({ kind: 'Resize', text: `Resize "${title}" (ignored on mobile)` });
        break;
      case 'configure':
        lines.push({ kind: 'Configure', text: `Change settings on "${title}"` });
        break;
      default:
        break;
    }
  }
  return lines;
}

/** The governed default for this user, used by reset-to-default. */
export function governedDefault(ctx: EntitlementContext): readonly MobileWidgetId[] {
  const entitled = new Set(entitledWidgetIds(ctx));
  return enforcePinning(defaultWidgetOrder().filter((id) => entitled.has(id)));
}
